"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { ImagePlaceholder } from "@/components/ui/image-placeholder";

interface Campaign {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  orgName: string | null;
  locationText?: string;
  coverImageUrl?: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  createdAt: string;
  participantCount: number;
  clueCount: number;
}

interface CampaignListSectionProps {
  tenant: {
    slug: string;
    primaryColor: string;
    accentColor?: string;
  };
  campaigns: Campaign[];
  title?: string | null;
  description?: string | null;
}

/**
 * Campaign List Section Component for Tenant Pages
 *
 * Displays a filterable grid of all campaigns. This is a core section
 * that cannot be hidden or removed from tenant pages.
 */
export function CampaignListSection({
  tenant,
  campaigns,
  title,
  description,
}: CampaignListSectionProps) {
  const [showArchived, setShowArchived] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // Filter campaigns based on selected filters
  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns;

    // Filter by archived status
    if (!showArchived) {
      filtered = filtered.filter((campaign) => campaign.status !== "ARCHIVED");
    }

    // Filter by specific status
    if (selectedStatus !== "all") {
      filtered = filtered.filter((campaign) => campaign.status === selectedStatus);
    }

    return filtered;
  }, [campaigns, showArchived, selectedStatus]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "bg-blue-100 text-blue-800";
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "ENDED":
        return "bg-red-100 text-red-800";
      case "ARCHIVED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "Published";
      case "ACTIVE":
        return "Active";
      case "ENDED":
        return "Ended";
      case "ARCHIVED":
        return "Archived";
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString();
  };

  const isActive = (campaign: Campaign) => {
    const now = new Date();
    const start = campaign.startAt ? new Date(campaign.startAt) : null;
    const end = campaign.endAt ? new Date(campaign.endAt) : null;

    return (
      campaign.status === "ACTIVE" ||
      (campaign.status === "PUBLISHED" &&
        (!start || start <= now) &&
        (!end || end >= now))
    );
  };

  const displayTitle = title || "All Scavenger Hunts";

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2
            className="text-3xl font-bold"
            style={{ color: 'var(--theme-text, #111827)' }}
          >
            {displayTitle}
          </h2>
          {description && (
            <p
              className="mt-2"
              style={{ color: 'var(--theme-text, #6B7280)', opacity: 0.8 }}
            >
              {description}
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label
              className="text-sm font-medium"
              style={{ color: 'var(--theme-text, #374151)' }}
            >
              Status:
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              style={{
                backgroundColor: 'var(--theme-bg, #FFFFFF)',
                color: 'var(--theme-text, #111827)',
              }}
            >
              <option value="all">All</option>
              <option value="PUBLISHED">Published</option>
              <option value="ACTIVE">Active</option>
              <option value="ENDED">Ended</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              id="showArchived"
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 focus:ring-2 border-gray-300 rounded"
              style={{ accentColor: 'var(--theme-primary, #3B82F6)' }}
            />
            <label
              htmlFor="showArchived"
              className="ml-2 text-sm"
              style={{ color: 'var(--theme-text, #374151)' }}
            >
              Show archived
            </label>
          </div>
        </div>
      </div>

      {/* Campaigns Grid */}
      {filteredCampaigns.length === 0 ? (
        <div className="text-center py-12">
          <p style={{ color: 'var(--theme-text, #6B7280)', opacity: 0.8 }}>
            No campaigns match your current filters.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden"
              style={{ backgroundColor: 'var(--theme-bg, #FFFFFF)' }}
            >
              {/* Campaign Cover Image */}
              <div className="w-full h-48">
                <ImagePlaceholder
                  src={campaign.coverImageUrl}
                  alt={campaign.title}
                  className="w-full h-full"
                  aspectRatio="wide"
                />
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}
                  >
                    {getStatusLabel(campaign.status)}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: 'var(--theme-text, #9CA3AF)' }}
                  >
                    {formatDate(campaign.createdAt)}
                  </span>
                </div>

                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ color: 'var(--theme-text, #111827)' }}
                >
                  {campaign.title}
                </h3>

                <p
                  className="mb-4 line-clamp-2 whitespace-pre-line"
                  style={{ color: 'var(--theme-text, #6B7280)', opacity: 0.8 }}
                >
                  {campaign.description}
                </p>

                <div
                  className="flex justify-between text-sm mb-4"
                  style={{ color: 'var(--theme-text, #9CA3AF)' }}
                >
                  <span>{campaign.clueCount} locations</span>
                  <span>{campaign.participantCount} participants</span>
                </div>

                {campaign.locationText && (
                  <p
                    className="text-xs mb-4"
                    style={{ color: 'var(--theme-text, #9CA3AF)' }}
                  >
                    {campaign.locationText}
                  </p>
                )}

                <div className="flex gap-2">
                  <Link
                    href={`/${tenant.slug}/campaigns/${campaign.slug}`}
                    className="flex-1 text-center py-2 px-4 rounded-md text-sm font-medium text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: tenant.primaryColor || '#3B82F6' }}
                  >
                    {isActive(campaign) ? 'Start Hunt' : 'View Hunt'}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
